/**
 * Shape of the runtime-resolved configuration fetched from
 * `/assets/config.json` (or `/assets/config.${env}.json`) at boot.
 *
 * Deliberately distinct from the build-time `environment.ts`:
 *
 * - **Build-time** (`environment.ts`) — fields that drive dead-code
 *   elimination (`production`), module-load-time constants (`devAreaSlug`,
 *   `filingLoaderMode`), and product UX flags that rarely differ per env.
 *
 * - **Runtime** (this file) — deployment-coupled values (API host, SignalR
 *   hub, dev-area public key) that ops needs to rotate without rebuilding.
 *
 * See `docs/deployment/runtime-config.md` for the VM deployment workflow
 * and `runtime-config.service.ts` for the consumer-facing signals.
 */
export interface RuntimeConfig {
  /** Base URL of the REST API, e.g. `http://localhost:5273/api`. */
  apiUrl: string;

  /** Base URL of the SignalR hubs, e.g. `http://localhost:5273/hubs`. */
  apiUrlHub: string;

  /** API version segment used by form-submit endpoints, e.g. `v2`. */
  apiVersion: string;

  /**
   * Developer-area transport encryption. Base64 SubjectPublicKeyInfo
   * (SPKI) DER of the dev JWT RSA public key. Not a secret — the private
   * half never leaves the server. Empty string disables dev-login in this
   * environment.
   */
  devAuth: {
    publicKey: string;
  };

  /**
   * When `true`, auth form inputs emit real `autocomplete` tokens
   * (`username`, `current-password`, `new-password`) so browsers can
   * offer autofill / save-password prompts. When `false`, they emit
   * `autocomplete="off"` instead.
   *
   * Developer Note (2026-04-23): The product owner is aware that missing
   * `autocomplete` attributes trigger a Chromium console warning but has
   * not officiated a fix. This flag exists so devs can keep autofill in
   * dev/staging (reducing repetitive credential re-entry) while ops can
   * disable it per-deployment. The console warning itself is NOT
   * suppressed — it remains informative regardless of this flag's value,
   * since an `autocomplete` attribute is always emitted (the value
   * changes, not its presence).
   */
  authAutocompleteEnabled: boolean;

  /**
   * When `true`, activates the cache-debug instrumentation: a diagonal-
   * stripe overlay + fixed "CACHE DEBUG ON" pill across the app, plus
   * `console.debug` logs at every cache-touching seam (HTTP interceptor,
   * runtime-config fetch, boot probes, BFCache restore). Off by default
   * everywhere except local dev; ops flips it on staging / UAT / prod
   * via `/etc/mbrs/frontend-overrides.json` when investigating a
   * "deploy not reflected" report. Zero runtime cost when `false`.
   *
   * See `docs/deployment/runtime-config.md` *Cache-debug flag* for the
   * operator flow and the list of logging seams it gates.
   */
  cacheDebug: boolean;

  /**
   * Shows the Retry button on the filing page's load-failure panel (see
   * `FilingLoadFailurePanelComponent`). The panel itself — blank sheet area
   * plus the diagnostic code — renders either way; this hides only Retry.
   *
   * Developer Note (2026-10-05): Retry is pending P.O. scope approval, so it
   * ships dark: `true` in local dev only, `false` in every deployed template.
   * Flip it per VM through `/etc/mbrs/frontend-overrides.json` to demo it.
   *
   * See `docs/deployment/runtime-config.md` *`filingLoadRetryEnabled`*.
   */
  filingLoadRetryEnabled: boolean;

  /**
   * Chooses between the REFACTORED load-failure diagnostics and the previous
   * behaviour, per environment.
   *
   * - `true` (staging / UAT / dev): the `MBRS-…` diagnostic codes in every
   *   load-failure toast — the filing-sheet toast (`SheetLoadTraceService`), the
   *   page-level toasts (Dashboard, XBRL Files, Profile, User Management,
   *   filing preview) and the AUTH toast on the login page.
   * - `false` (production): the previous, vague behaviour. A conversion-sheet
   *   load failure toasts one of the three original generic sentences (see
   *   `LEGACY_SHEET_LOAD_FAILURE_COPY`), and the page-level and login-page AUTH
   *   toasts do not appear at all, as on `main` before this work. Everything
   *   else the diagnostics work changed (stay-on-page failure panel, redirects,
   *   session gate, console records with the full code) behaves identically in
   *   both modes — this flag changes only what the USER READS in a toast.
   *
   * Developer Note (2026-10-14): TEMPORARY. Production is not yet cleared to
   * receive the coded toasts, so v1.0.3 ships with this `false`. Once
   * production is also cleared to have the refactored diagnostics officially
   * land, REMOVE this flag: delete the key (types, `REQUIRED_RUNTIME_CONFIG_KEYS`,
   * validation, the four `config/config.*.json` templates, `src/assets/config.json`,
   * `docs/deployment/runtime-config.md`), keep the `true` branches, and delete
   * `utils/legacy-sheet-load-failure-copy.util.ts`. Every site is tagged
   * `REFACTORED-DIAGNOSTIC-CODES-FLAG` — `grep -rn "refactoredDiagnosticCodesEnabled\|REFACTORED-DIAGNOSTIC-CODES-FLAG\|LEGACY_SHEET_LOAD_FAILURE_COPY" src scripts docs config`
   * finds them all.
   *
   * See `docs/deployment/runtime-config.md` *`refactoredDiagnosticCodesEnabled`*.
   */
  refactoredDiagnosticCodesEnabled: boolean;

  /**
   * Gates the noisy `[CrossTabSync]…` `console.log` instrumentation emitted
   * from `CrossTabSyncService`, `FilingFormStateService.notifyCrossTab` /
   * `applyRemoteChange`, and the per-role `remote-validation-triggered`
   * subscribers. Off by default everywhere except local dev — flip it on
   * staging / UAT via `/etc/mbrs/frontend-overrides.json` when investigating
   * a multi-tab sync issue.
   *
   * Has NO effect in production builds: the call sites are wrapped in a
   * build-time `!environment.production` outer guard so the entire branch
   * is dead-code-eliminated from the production bundle. The runtime flag
   * is the inner gate that controls visibility in non-prod environments.
   *
   * See `docs/deployment/runtime-config.md` *Cross-tab-sync debug flag*.
   */
  crossTabSyncDebug: boolean;

  /**
   * Gates the `[x-sheet]…` `console` instrumentation emitted from the
   * cross-sheet equality comparators (`EqualityValidationService`,
   * `EquityReconciliationService`, `CashFlowReconciliationService` via
   * `CrossSheetCalcDebugService`). Each logged comparison prints the raw vs
   * 2-d.p.-rounded operands, the raw/rounded difference, the tolerance, and a
   * stale-operand vs rounding-artifact verdict — so a
   * "values look equal but a mismatch error shows" report can be diagnosed in
   * a live filing. Off by default everywhere; flip it on staging / UAT via
   * `/etc/mbrs/frontend-overrides.json` when investigating such a report.
   *
   * Has NO effect in production builds: `CrossSheetCalcDebugService` short-circuits
   * on a build-time `!environment.production` guard before reading this flag,
   * so the branch is dead-code-eliminated from the production bundle. The
   * runtime flag is the inner gate for non-prod environments.
   *
   * See `docs/deployment/runtime-config.md` *Cross-sheet calc debug flag*.
   */
  crossSheetCalcDebug: boolean;

  /**
   * Gates the `seance` liveness-probe diagnostic harness — the `localStorage`
   * timeline (`yyc-liveness-probe-diag:<tab>` keys) written by
   * `liveness-probe-diagnostics.ts` from `FilingFormStateService`,
   * `CrossTabSyncService` and the XBRL Files listing, plus the
   * `__livenessDiag()` / `__livenessDiagClear()` console helpers that read it.
   *
   * Unlike the other debug flags this one gates **writes to storage**, not
   * console noise: the harness exists precisely because the tabs under
   * investigation are closed as part of the repro. Leaving it on costs a
   * `localStorage` read-modify-write on every hydrate, probe, ping, sheet
   * release and unload — cheap, but not free, and the buffer survives logout
   * by design (its key prefix sits outside the `mbrs-` namespace
   * `clearCustomerDataCache()` wipes). Turning it off stops recording AND
   * stops the readers, so an operator who flips it mid-round should dump
   * first.
   *
   * Has NO effect in production builds: `liveness-probe-diagnostics.ts`
   * short-circuits on a build-time `!environment.production` guard before
   * reading this flag, so the branch is dead-code-eliminated from the
   * production bundle. The runtime flag is the inner gate for non-prod
   * environments — which is the whole point, since UAT is where the
   * last-tab-out discard defect is being reproduced.
   *
   * **Temporary, like the harness it gates.** Both come out together once the
   * probe defect is closed; deleting the key is then part of that removal.
   *
   * Committed defaults: `true` in dev only. Staging / UAT / production ship
   * `false`, so the harness is **opt-in per deployment** — an operator
   * reproducing the defect on a UAT VM turns it on via
   * `/etc/mbrs/frontend-overrides.json` and off again afterwards, with no
   * rebuild either way. This is narrower than the pre-flag behaviour, where
   * every non-production build recorded unconditionally.
   *
   * See `docs/deployment/runtime-config.md` *`livenessProbeDebug`*.
   */
  livenessProbeDebug: boolean;

  /**
   * When `true`, renders the per-field info-circle tooltip (driven by
   * `control.ui.note` in the role schemas) next to monetary line items
   * in the role15 Statement of Comprehensive Income form. When `false`,
   * the icon is suppressed entirely so end users in UAT / production do
   * not see developer-oriented field explanations.
   *
   * Lives in runtime config (rather than `environment.*.ts` or the
   * existing `EnvironmentService.isDevelopment()` gate) so ops can flip
   * it per-deployment via `/etc/mbrs/frontend-overrides.json` without a
   * rebuild — useful when a tester or trainer needs the explanations
   * surfaced on a UAT VM for a single session.
   *
   * Committed defaults: `true` in dev + staging (internal), `false` in
   * UAT + production (end-user-facing).
   */
  fieldNoteTooltipsEnabled: boolean;

  /**
   * When `false`, no mandatory-field `*` is painted anywhere in the filing
   * form. When `true`, the marker renders exactly as it did before this flag
   * existed.
   *
   * **Presentation only.** Mandatory fields stay mandatory and still block
   * submit — this gates `RequiredMarkerComponent`, never
   * `isMandatoryForValidation` in `utils/required-field.util.ts`. Marking and
   * enforcing are different questions; do not "align" them.
   *
   * Committed default is `false` on ALL four templates (PO, 2026-08-11: remove
   * the markers unilaterally). It lives in runtime config rather than a
   * constant so a PO reversal is an ops flip via
   * `/etc/mbrs/frontend-overrides.json` plus a deploy — no code change, and no
   * re-audit of the 41 schemas against the Govt. Prep Tool.
   *
   * See `docs/deployment/runtime-config.md` *`mandatoryMarkersEnabled`*.
   */
  mandatoryMarkersEnabled: boolean;

  /**
   * When `true`, the XBRL files listing offers **Download** on conversions
   * that are not a clean success — `Review` and `Failed` — in addition to
   * `Success` / `Success*`. On `Failed` this also makes the more-actions menu
   * appear at all, since that row carries no menu by default.
   *
   * Never widens to `InProgress`, `PendingValidation` or `AwaitingFiles`: the
   * conversion has produced no artifact at those statuses, so the affordance
   * could only fail. The allow-lists live in
   * `src/app/utils/conversion-status.util.ts`.
   *
   * **Temporary QA measure, not a product decision.** Requested 2026-09-03 so
   * QA can pull the generated XBRL of non-success conversions and cross-check
   * it against the source documents. The committed production default is
   * `true` for the duration of that exercise; ops flips it back to `false`
   * from the VM (`/etc/mbrs/frontend-overrides.json`) once QA signs off — no
   * rebuild needed, which is the whole reason it lives in runtime config
   * rather than a constant. Downloading a `Failed` conversion may legitimately
   * surface the "Could not download the XBRL file" toast if the backend never
   * produced one; that risk was accepted when the flag was requested.
   *
   * Committed defaults: `false` in dev / staging / UAT, `true` in production.
   *
   * See `docs/deployment/runtime-config.md` *`nonSuccessDownloadEnabled`*.
   */
  nonSuccessDownloadEnabled: boolean;

  /**
   * When `true`, the conversion-engine picker is rendered beside **Convert to
   * XBRL** on the dashboard and beside **Delete** on the Filing Prerequisites
   * and Filing Information sheets, letting the user choose which engine
   * processes their filing.
   *
   * When `false` the feature is entirely absent: the picker does not render
   * AND `ConversionEngineService.ensureLoaded()` makes no network call, so a
   * deployment with the feature off pays nothing for it. Submits then omit the
   * engine field and the server applies its configured `Conversion:Engine`,
   * which is the pre-feature behaviour.
   */
  enginePickerEnabled: boolean;

  /**
   * When `true`, submitting the Reset Password form skips the backend call and
   * navigates straight to the **Failed** render of the post-reset status page
   * (`ResetPasswordComponent.submitForm()`). The password is NOT changed and no
   * "reset successful" email is sent, so what the screen says is what happened.
   *
   * Exists so QA / P.O. can see the Failed screen on demand without breaking a
   * real reset link. It forces the generic Failed copy, not the expired-link
   * variant.
   *
   * **Disabled in production builds.** `RuntimeConfigService.validate()`
   * rejects `true` when `environment.production` is set and aborts the boot
   * naming this key, rather than quietly ignoring it — a stray `true` in a
   * production overrides file is a misconfiguration the operator must see.
   * Committed default is `false` in every template.
   *
   * See `docs/deployment/runtime-config.md` *`forceResetPasswordFailure`*.
   */
  forceResetPasswordFailure: boolean;

  /**
   * QA switch. When `true` (non-production builds only), the app honours a
   * per-browser "simulate a server failure" scenario, so QA can smoke-test the
   * SRV (HTTP 5xx) toast without a code change or a backend fault. The scenario
   * itself is chosen in the browser console (`__simulateSrv('<scope>', 500)`),
   * stored in `localStorage` under `yyc-simulate-failure` and expires after an
   * hour. This flag only ENABLES the feature on an environment.
   *
   * **Disabled in production builds.** `RuntimeConfigService.validate()` rejects
   * `true` when `environment.production` is set and aborts the boot naming this
   * key; the interceptor is also never registered in a production build. Committed
   * default is `false` in every deployed template.
   *
   * See `docs/deployment/runtime-config.md` *`simulateFailureEnabled`*.
   */
  simulateFailureEnabled: boolean;

  /**
   * Number of rows each server-paginated listing table requests per page.
   *
   * Both listings are **lazy** (`[paginator]="false"` on the table, a
   * standalone `<p-paginator>` in the footer): the value below is sent to
   * the backend as the `pageSize` query param on every fetch, and the
   * backend's `totalCount` sizes the paginator. So changing a number here
   * changes what the server returns — it is not a client-side slice.
   *
   * Lives in runtime config (rather than `environment.*.ts`) so ops can
   * tune density per-deployment via `/etc/mbrs/frontend-overrides.json`
   * without a rebuild — e.g. dropping `xbrlFiles` on a VM where the
   * listing query is slow, or raising it for a demo on a large screen.
   *
   * The committed defaults (15 / 9) are the values these tables were
   * hardcoded to before this key existed, so a default deployment
   * paginates exactly as it always did.
   *
   * Note: `generate-config.mjs` merges `config.<env>.local.json` over the
   * template with a **shallow** merge, so an override file that sets this
   * key must restate BOTH members — a partial group replaces the whole
   * object and would fail validation at boot.
   */
  listingPageSize: {
    /** XBRL files listing (`xbrl-files.component.ts`). */
    xbrlFiles: number;

    /** Admin user-management listing (`user-management.component.ts`). */
    userManagement: number;
  };
}

/**
 * Top-level keys the runtime validator requires to be present and
 * well-typed before bootstrap is allowed to proceed. Kept next to the
 * interface so adding a field is a two-line change.
 */
export const REQUIRED_RUNTIME_CONFIG_KEYS = [
  'apiUrl',
  'apiUrlHub',
  'apiVersion',
  'devAuth',
  'authAutocompleteEnabled',
  'cacheDebug',
  'filingLoadRetryEnabled',
  'refactoredDiagnosticCodesEnabled',
  'crossTabSyncDebug',
  'crossSheetCalcDebug',
  'livenessProbeDebug',
  'fieldNoteTooltipsEnabled',
  'mandatoryMarkersEnabled',
  'nonSuccessDownloadEnabled',
  'enginePickerEnabled',
  'forceResetPasswordFailure',
  'simulateFailureEnabled',
  'listingPageSize',
] as const satisfies readonly (keyof RuntimeConfig)[];
